// src/components/equipements/BadgeMontage.tsx — SCE Issues
// Le montage sous-traité d'un équipement, en badge à côté de « En fabrication »
// (29/10, maquette https://claude.ai/artifact/PQFFkpfGUpLAJFos3qfLLd) :
// « Élec · Actemium ». Un simple badge (Guillaume) : l'attribution se règle
// sur la page « Montage sous-traité », où un clic mène les coordinateurs.
// « À attribuer » en pointillé, pour eux seulement ; rien pour « En interne ».
// Contour gris, jamais coloré : le badge ne dit ni une gravité ni un métier.

import { Handshake } from 'lucide-react'
import type { CSSProperties } from 'react'
import { LIBELLE_MONTAGE, type Attribution, type MetierMontage } from '@/utils/montages'
import { LogoSociete } from '@/components/ui/LogoSociete'

const BASE: CSSProperties = {
  gap: 5,
  padding: '2px 8px 2px 6px',
  borderRadius: 4,
  border: '1px solid var(--color-gray-30)',
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-text-primary)',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  fontFamily: 'inherit',
}

const A_ATTRIBUER: CSSProperties = {
  ...BASE,
  padding: '2px 8px',
  borderStyle: 'dashed',
  borderColor: 'var(--color-gray-40)',
  color: 'var(--color-text-secondary)',
  fontWeight: 400,
}

function dateCourte(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR')
}

export function BadgeMontage({
  metier,
  attribution,
  logo,
  onClick,
}: {
  metier: MetierMontage
  /** null : à attribuer. */
  attribution: Attribution | null
  /** Logo de la société (29/10), à la place de la poignée de main. */
  logo?: string | null
  /** Coordinateurs : ouvre la page « Montage sous-traité ». */
  onClick?: () => void
}): JSX.Element {
  const quoi = LIBELLE_MONTAGE[metier]
  const titre = attribution
    ? `Montage ${quoi} confié à ${attribution.valeur}${attribution.par ? ` par ${attribution.par}` : ''}${attribution.le ? ` le ${dateCourte(attribution.le)}` : ''}`
    : `Montage ${quoi} pas encore attribué`
  const contenu = attribution ? (
    <>
      {logo ? <LogoSociete id={logo} hauteur={14} largeurMax={40} /> : <Handshake size={13} strokeWidth={2.2} aria-hidden="true" />}
      <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>{quoi}</span>
      {attribution.valeur}
    </>
  ) : (
    `${quoi} à attribuer`
  )
  const style = attribution ? BASE : A_ATTRIBUER
  if (!onClick) {
    return (
      <span className="inline-flex items-center whitespace-nowrap" style={style} title={titre}>
        {contenu}
      </span>
    )
  }
  return (
    <button
      type="button"
      className="inline-flex items-center whitespace-nowrap badge-montage"
      style={{ ...style, cursor: 'pointer' }}
      title={`${titre} · régler dans « Montage sous-traité »`}
      onClick={onClick}
    >
      {contenu}
    </button>
  )
}
